import { MarketplaceSettings, marketplaceSettingEnabled } from './marketplace-settings'

// MYS-7358: editing product content across marketplace boundaries is opt-in on both sides.
// The selected marketplace must enable cross-marketplace editing, while a provider's home
// marketplace can explicitly protect all of its product content from foreign operators.

function clientAssignments(entry: any): any[] {
	return entry?.fields?.clientAssignments?.de ?? []
}

export function homeClient(serviceProvider: any): any | undefined {
	return clientAssignments(serviceProvider)
		.find(assignment => assignment?.fields?.isHomebase?.de === true)
		?.fields?.client?.de
}

export function homeClientId(serviceProvider: any): string | undefined {
	return homeClient(serviceProvider)?.sys?.id
}

export function productIsAssignedToClient(product: any, clientId: string): boolean {
	return clientAssignments(product).some(assignment =>
		assignment?.fields?.client?.de?.sys?.id === clientId
	)
}

export function mayEditProductFieldsAsOperator({
	isHomeOperator,
	selectedClient,
	product,
	serviceProvider,
}: {
	isHomeOperator: boolean
	selectedClient?: any
	product?: any
	serviceProvider?: any
}): boolean {
	if (isHomeOperator) return true
	const selectedClientId = selectedClient?.sys?.id
	if (!selectedClientId) return false
	if (!Array.isArray(selectedClient?.fields?.marketplaceSettings?.de)) return false
	if (!marketplaceSettingEnabled(selectedClient, MarketplaceSettings.CROSS_MARKETPLACE_PRODUCT_EDITING_ENABLED)) return false
	if (!productIsAssignedToClient(product, selectedClientId)) return false

	const providerHomeClient = homeClient(serviceProvider)
	if (!providerHomeClient?.sys?.id) return false
	// Requiring the settings collection to be resolved avoids treating partially hydrated client
	// data as an unprotected marketplace.
	if (!Array.isArray(providerHomeClient?.fields?.marketplaceSettings?.de)) return false
	return !marketplaceSettingEnabled(providerHomeClient, MarketplaceSettings.CROSS_MARKETPLACE_PRODUCT_EDITING_PROTECTED)
}

export enum ProductEditScope {
	FULL = 'full',
	OWN_LISTING = 'ownListing',
	NONE = 'none',
}

// MYS-7453: what an operator may change on a product in the selected marketplace. Managing that
// marketplace's own listing (publishing, categories, taking it offline there) only needs the product
// to be assigned to it. The shared content follows the cross-marketplace rule above, and a protected
// home marketplace keeps its content locked for marketplaces that have not opted in as well.
export function operatorProductEditScope({
	isHomeOperator,
	selectedClient,
	product,
	serviceProvider,
}: {
	isHomeOperator: boolean
	selectedClient?: any
	product?: any
	serviceProvider?: any
}): ProductEditScope {
	if (isHomeOperator) return ProductEditScope.FULL
	const selectedClientId = selectedClient?.sys?.id
	if (!selectedClientId) return ProductEditScope.NONE

	const ownListing = productIsAssignedToClient(product, selectedClientId) ? ProductEditScope.OWN_LISTING : ProductEditScope.NONE
	if (marketplaceSettingEnabled(selectedClient, MarketplaceSettings.CROSS_MARKETPLACE_PRODUCT_EDITING_ENABLED)) {
		return mayEditProductFieldsAsOperator({ isHomeOperator, selectedClient, product, serviceProvider }) ? ProductEditScope.FULL : ownListing
	}
	// not opted in: the operator flows that predate MYS-7358 stay, unless the home marketplace protects its content
	return marketplaceSettingEnabled(homeClient(serviceProvider), MarketplaceSettings.CROSS_MARKETPLACE_PRODUCT_EDITING_PROTECTED)
		? ownListing
		: ProductEditScope.FULL
}

// MYS-7358 follow-up: deleting, deactivating and archiving a product are NOT client-scoped -
// productService.removeProduct() drops the entry entirely and deactivateProduct() walks every
// clientAssignment. Granting those to a foreign operator under the editing opt-in alone would let
// one marketplace take down another marketplace's listing, which is exactly what MYS-3927 set out
// to prevent. So a non-home operator only gets the lifecycle actions while the product is assigned
// to their marketplace and to no other - then the blast radius is their own marketplace.
export function productIsAssignedOnlyToClient(product: any, clientId?: string): boolean {
	// Without an id every unresolved assignment would compare equal to it, so fail closed.
	if (!clientId) return false
	const assignments = clientAssignments(product)
	if (!assignments.length) return false
	return assignments.every(assignment => assignment?.fields?.client?.de?.sys?.id === clientId)
}

export function mayManageProductLifecycleAsOperator({
	isHomeOperator,
	selectedClient,
	product,
	serviceProvider,
}: {
	isHomeOperator: boolean
	selectedClient?: any
	product?: any
	serviceProvider?: any
}): boolean {
	if (isHomeOperator) return true
	if (!mayEditProductFieldsAsOperator({ isHomeOperator, selectedClient, product, serviceProvider })) return false
	return productIsAssignedOnlyToClient(product, selectedClient?.sys?.id)
}
